import { cn } from '@siliconx/shared/ui/utils';

/** Per-row finding returned by POST /api/client/import/batches (SIL-1589). */
export interface ImportRowFinding {
  field: string;
  code: string;
  message: string;
}

export interface ImportRowErrors {
  rowNum: number;
  errors: ImportRowFinding[];
}

export interface ImportRowWarnings {
  rowNum: number;
  warnings: ImportRowFinding[];
}

/** Rows shown before collapsing the rest into "and N more". */
export const IMPORT_ISSUE_ROW_LIMIT = 25;

/**
 * Labeled per-row import findings: row number, field, message. `errors` are
 * rejected rows (red); `warnings` are accepted-but-suspicious rows (amber).
 */
export function ImportRowIssues({
  errors,
  warnings = [],
}: {
  errors: ImportRowErrors[];
  warnings?: ImportRowWarnings[];
}) {
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div className="space-y-3">
      {errors.length > 0 && (
        <IssueTable
          tone="error"
          title={`${errors.length} rejected row${errors.length === 1 ? '' : 's'} (not imported)`}
          rows={errors.flatMap((r) => r.errors.map((e) => ({ rowNum: r.rowNum, ...e })))}
        />
      )}
      {warnings.length > 0 && (
        <IssueTable
          tone="warning"
          title={`${warnings.length} row${warnings.length === 1 ? '' : 's'} accepted with warnings`}
          rows={warnings.flatMap((r) => r.warnings.map((w) => ({ rowNum: r.rowNum, ...w })))}
        />
      )}
    </div>
  );
}

function IssueTable({
  tone,
  title,
  rows,
}: {
  tone: 'error' | 'warning';
  title: string;
  rows: (ImportRowFinding & { rowNum: number })[];
}) {
  const shown = rows.slice(0, IMPORT_ISSUE_ROW_LIMIT);
  const hidden = rows.length - shown.length;
  return (
    <div
      data-testid={`import-${tone}s`}
      className={cn(
        'rounded-md border text-xs',
        tone === 'error' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
      )}
    >
      <p className={cn('px-3 py-2 font-medium', tone === 'error' ? 'text-red-800' : 'text-amber-800')}>
        {title}
      </p>
      <table className="w-full text-left">
        <thead>
          <tr className="text-muted-foreground">
            <th className="px-3 py-1 font-medium">Row</th>
            <th className="px-3 py-1 font-medium">Field</th>
            <th className="px-3 py-1 font-medium">Problem</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={`${r.rowNum}-${r.field}-${r.code}-${i}`} className="border-t border-border/50">
              <td className="px-3 py-1 tabular-nums">{r.rowNum}</td>
              <td className="px-3 py-1 font-mono">{r.field}</td>
              <td className="px-3 py-1">{r.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && <p className="px-3 py-1.5 text-muted-foreground">and {hidden} more</p>}
    </div>
  );
}
